import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import classnames from 'classnames';
import * as React from 'react';
import { Transition as InternalTransition } from 'react-transition-group';
import { noop } from '../utils';
import Events from '../utils/events';
var TransitionState;
(function (TransitionState) {
  TransitionState["UNMOUNTED"] = "unmounted";
  TransitionState["ENTER"] = "enter";
  TransitionState["ENTERING"] = "entering";
  TransitionState["ENTERED"] = "entered";
  TransitionState["EXIT"] = "exit";
  TransitionState["EXITING"] = "exiting";
  TransitionState["EXITED"] = "exited";
})(TransitionState || (TransitionState = {}));
var animationEndName = 'animationend';
var transitionEndName = 'transitionend';
var Transition = function Transition(props) {
  var _classnames;
  var visible = props.visible,
    tranisitionName = props.tranisitionName,
    duration = props.duration,
    forceRender = props.forceRender,
    destroy = props.destroy,
    children = props.children,
    _onEnter = props.onEnter,
    onEnterActive = props.onEnterActive,
    onEnterEnd = props.onEnterEnd,
    onLeave = props.onLeave,
    onLeaveActive = props.onLeaveActive,
    onLeaveEnd = props.onLeaveEnd;
  var nodeRef = React.useRef();
  var _React$useState = React.useState(TransitionState.UNMOUNTED),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    setState = _React$useState2[1];
  var callbackRef = React.useRef(noop);
  var unmounted = TransitionState.UNMOUNTED === state;
  var enter = TransitionState.ENTER === state;
  var entering = TransitionState.ENTERING === state;
  var leave = TransitionState.EXIT === state;
  var leaving = TransitionState.EXITING === state;
  var exited = TransitionState.EXITED === state;
  var running = enter || entering || leave || leaving;
  var className = classnames((_classnames = {}, _defineProperty(_classnames, "".concat(tranisitionName, "-enter"), enter || entering), _defineProperty(_classnames, "".concat(tranisitionName, "-enter-active"), entering), _defineProperty(_classnames, "".concat(tranisitionName, "-leave"), leave || leaving), _defineProperty(_classnames, "".concat(tranisitionName, "-leave-active"), leaving), _classnames));
  var timeout = running && duration && duration > 0 ? "".concat(props.duration, "ms") : undefined;
  var style = {
    animationDuration: timeout,
    WebkitAnimationDuration: timeout,
    transitionDuration: timeout,
    WebkitTransitionDuration: timeout,
    display: (unmounted || exited) && !visible && !destroy ? 'none' : undefined
  };
  var setNodeRef = function setNodeRef(node) {
    nodeRef.current = node;
  };
  React.useImperativeHandle(props.nodeRef, function () {
    return nodeRef.current;
  });
  React.useEffect(function () {
    return function () {
      if (!nodeRef.current) return;
      Events.off(nodeRef.current, animationEndName, callbackRef.current);
      Events.off(nodeRef.current, transitionEndName, callbackRef.current);
    };
  }, []);
  return /*#__PURE__*/React.createElement(InternalTransition, {
    nodeRef: nodeRef,
    in: visible,
    addEndListener: function addEndListener(next) {
      if (!nodeRef.current) return;
      callbackRef.current = function (event) {
        var target = event.target;
        if (!target.contains(nodeRef.current)) return;
        next();
      };
      Events.on(nodeRef.current, animationEndName, callbackRef.current);
      Events.on(nodeRef.current, transitionEndName, callbackRef.current);
    },
    mountOnEnter: !forceRender,
    unmountOnExit: destroy,
    onEnter: function onEnter() {
      setState(TransitionState.ENTER);
      _onEnter === null || _onEnter === void 0 ? void 0 : _onEnter();
    },
    onEntering: function onEntering() {
      setState(TransitionState.ENTERING);
      onEnterActive === null || onEnterActive === void 0 ? void 0 : onEnterActive();
    },
    onEntered: function onEntered() {
      setState(TransitionState.ENTERED);
      onEnterEnd === null || onEnterEnd === void 0 ? void 0 : onEnterEnd();
    },
    onExit: function onExit() {
      setState(TransitionState.EXIT);
      onLeave === null || onLeave === void 0 ? void 0 : onLeave();
    },
    onExiting: function onExiting() {
      setState(TransitionState.EXITING);
      onLeaveActive === null || onLeaveActive === void 0 ? void 0 : onLeaveActive();
    },
    onExited: function onExited() {
      setState(TransitionState.EXITED);
      onLeaveEnd === null || onLeaveEnd === void 0 ? void 0 : onLeaveEnd();
    }
  }, function () {
    if (/*#__PURE__*/React.isValidElement(children)) {
      return /*#__PURE__*/React.cloneElement(children, {
        ref: setNodeRef,
        className: classnames(children.props.className, className),
        style: _objectSpread(_objectSpread({}, children.props.style), style)
      });
    }
    return children === null || children === void 0 ? void 0 : children({
      className: className,
      style: style,
      visible: visible
    }, setNodeRef);
  });
};
export default Transition;